/**
 * Captions of the interpreter's phone call, shown in the room (Robert,
 * 2026-10-06: "automated captioning on the terp side follows their twilio
 * call exclusively, but is reported to the room").
 *
 * The interpreter's room decides when to caption: while the feature is on
 * (auth_success capabilities.phoneCaptions, PHONE_CAPTIONS_ENABLED), this
 * device is the interpreter on a call with a connected phone leg, and someone
 * else in the room has captions on (Jitsi's requestingTranscription property,
 * subtitles/middleware.ts). Robert, 2026-10-06: "start sending the request to
 * deepgram as soon as one deaf person turns on the captioning feature". The
 * server forks the phone leg to Deepgram (vrs-server
 * src/lib/phone-caption-stream.ts) and sends each result back here as
 * phone_caption; this room shows it to everyone as a caption line.
 *
 * Pure logic here, no React Native or Jitsi code; phoneCaptionsMiddleware.ts
 * wires it.
 */
/** Captions stop this long after the last person turned theirs off, so a quick toggle does not restart them. */
export const PHONE_CAPTIONS_STOP_DELAY_MS = 10000;
/** After a refusal (other than "no phone call yet"), wait this long before asking again. */
export const PHONE_CAPTIONS_RETRY_MS = 30000;
/** The participant name these caption lines carry on the wire (the room shows a speaker label instead). */
export const PHONE_CAPTION_SPEAKER = 'Phone';
/** Every phone caption line's message_id starts with this (one id per utterance). */
export const PHONE_CAPTION_ID_PREFIX = 'phone-';
export const PHONE_CAPTION_LABELS = {
    caller: 'Caller',
    youSpoken: 'You · spoken',
    youVoiced: 'You · voiced'
};
/**
 * Whether a caption line in the room is a phone caption (phoneCaptionMessage).
 *
 * @param {string} messageId - The caption line's message_id.
 * @returns {boolean}
 */
export function isPhoneCaptionId(messageId) {
    return typeof messageId === 'string' && messageId.startsWith(PHONE_CAPTION_ID_PREFIX);
}
/**
 * Who said a phone caption line (captions-web.html frame 3, 2026-10-29):
 * the hearing party by the contact's name, else "Caller"; the interpreter's
 * voice is "You · voiced" (they voice for the Deaf person); the Deaf
 * person's own voice is "You · spoken".
 *
 * @param {string} track - The line's track.
 * @param {IPhoneCaptionLabels} labels - The label words.
 * @param {string} [contactName] - The hearing party's contact name, when the room knows it.
 * @returns {string}
 */
export function phoneCaptionSpeaker(track, labels = PHONE_CAPTION_LABELS, contactName) {
    if (track === 'interpreter') {
        return labels.youVoiced;
    }
    if (track === 'deaf') {
        return labels.youSpoken;
    }
    const name = typeof contactName === 'string' ? contactName.trim() : '';
    return name || labels.caller;
}
/**
 * Creates the controller. Update() with a fresh snapshot after each change
 * (a participant's captions, the phone leg, the call, the capability);
 * serverState() with each phone_captions_started / _stopped / error;
 * reconnected() after the socket signed in again..
 *
 * @param {IPhoneCaptionsDeps} deps - Timers, the clock and the socket.
 * @returns {Object}
 */
export function createPhoneCaptionsController(deps) {
    let requestedFor;
    let stopTimer;
    let retryAfter = 0;
    let last = { enabled: false,
        interpreter: false,
        phoneLive: false,
        wanted: false };
    const wants = (snapshot) => Boolean(snapshot.enabled && snapshot.interpreter
        && snapshot.callId && snapshot.phoneLive && snapshot.wanted);
    const cancelStop = () => {
        if (stopTimer !== undefined) {
            deps.clearTimeout(stopTimer);
            stopTimer = undefined;
        }
    };
    const stopNow = () => {
        cancelStop();
        if (requestedFor) {
            deps.send(requestedFor, false);
        }
        requestedFor = undefined;
    };
    const start = (callId) => {
        if (deps.now() < retryAfter) {
            return;
        }
        if (deps.send(callId, true)) {
            requestedFor = callId;
        }
    };
    /**
     * A fresh look at the room, the phone leg and the call.
     *
     * @param {IPhoneCaptionsSnapshot} snapshot - What is true now.
     * @returns {void}
     */
    function update(snapshot) {
        last = snapshot;
        // Another call (or none): the old one's captions stop at once.
        if (requestedFor && requestedFor !== snapshot.callId) {
            stopNow();
        }
        if (wants(snapshot)) {
            cancelStop();
            if (!requestedFor) {
                start(snapshot.callId);
            }
            return;
        }
        if (!requestedFor) {
            return;
        }
        // The phone leg or the feature went away: stop now. Only the people
        // asking for captions get a moment to change their mind.
        if (!snapshot.phoneLive || !snapshot.enabled || !snapshot.interpreter) {
            stopNow();
            return;
        }
        if (stopTimer === undefined) {
            stopTimer = deps.setTimeout(() => {
                stopTimer = undefined;
                if (!wants(last)) {
                    stopNow();
                }
            }, PHONE_CAPTIONS_STOP_DELAY_MS);
        }
    }
    /**
     * The server's answer: running, stopped, or refused with a code.
     *
     * @param {Object} answer - The phone_captions_started / _stopped / error answer.
     * @returns {void}
     */
    function serverState({ callId, code, running }) {
        if (running || !callId || callId !== requestedFor) {
            return;
        }
        requestedFor = undefined;
        // "No phone call yet" is asked again when the phone leg connects;
        // anything else waits a little before the next try.
        if (code && code !== 'NO_PHONE_LEG') {
            retryAfter = deps.now() + PHONE_CAPTIONS_RETRY_MS;
        }
    }
    /**
     * The socket signed in again: ask again (the server answers a repeat with
     * the running captions).
     *
     * @returns {void}
     */
    function reconnected() {
        if (requestedFor && wants(last)) {
            deps.send(requestedFor, true);
        }
        else if (!requestedFor) {
            update(last);
        }
    }
    return {
        reconnected,
        requestedFor: () => requestedFor,
        serverState,
        stop: stopNow,
        update
    };
}
/**
 * The caption line the room shows for one phone caption: the Jitsi
 * transcription-result endpoint message that subtitles/middleware.ts reads.
 * One message_id per utterance (the server's segmentId), so interims are
 * replaced in place by their final. Attributed to this interpreter's own
 * participant (the viewers' trust boundary), named "Phone", with its track.
 * The room shows the speaker from the track (phoneCaptionSpeaker), never
 * "Phone (Hearing)".
 *
 * @param {Object} caption - The phone_caption payload.
 * @param {string} localParticipantId - This interpreter's participant id.
 * @param {number} timestamp - Now.
 * @returns {Object}
 */
export function phoneCaptionMessage(caption, localParticipantId, timestamp) {
    return {
        captionTone: null,
        ...typeof caption.confidence === 'number' ? { confidence: caption.confidence } : {},
        is_interim: !caption.isFinal,
        language: 'en-US',
        message_id: `${PHONE_CAPTION_ID_PREFIX}${caption.segmentId}`,
        participant: {
            avatar_url: '',
            id: localParticipantId,
            name: PHONE_CAPTION_SPEAKER
        },
        source: 'twilio-phone-leg',
        stability: caption.isFinal ? 1 : 0.7,
        timestamp,
        track: caption.track,
        transcript: [{ text: caption.text }],
        type: 'transcription-result'
    };
}
/**
 * Whether someone else in the room has captions on: Jitsi's
 * requestingTranscription participant property ('true' in presence).
 * Hidden participants (recorders, transcribers) do not count.
 *
 * @param {Array} participants - The conference's remote participants.
 * @returns {boolean}
 */
export function someoneWantsCaptions(participants) {
    return participants.some(participant => {
        if (typeof participant?.isHidden === 'function' && participant.isHidden()) {
            return false;
        }
        const value = typeof participant?.getProperty === 'function'
            ? participant.getProperty('requestingTranscription')
            : undefined;
        return value === true || value === 'true';
    });
}
